import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-toastify';
import { useGoogleLogin } from '@react-oauth/google';
import { createPortal } from 'react-dom';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useLogin, useRegister } from '../../hooks/queries/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import apiService from '../../services/apiService';
import analyticsService from '../../services/analyticsService';
import logo from '../../assets/images/logo.png';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(6, 'Min 6 characters'),
});

const registerSchema = z
  .object({
    firstName: z.string().min(1, 'Required'),
    lastName: z.string().min(1, 'Required'),
    email: z.string().email('Enter a valid email'),
    password: z.string().min(8, 'Min 8 chars').regex(/[A-Z]/, 'Need uppercase').regex(/[0-9]/, 'Need number'),
    confirmPassword: z.string().min(1, 'Required'),
  })
  .refine((d) => d.password === d.confirmPassword, { message: "Passwords don't match", path: ['confirmPassword'] });

type LoginData = z.infer<typeof loginSchema>;
type RegisterData = z.infer<typeof registerSchema>;

export default function AuthModal() {
  const { activeModal, closeModal } = useUIStore();
  const isOpen = activeModal === 'auth-login' || activeModal === 'auth-register';
  const [view, setView] = useState<'login' | 'register'>('login');
  const navigate = useNavigate();

  useEffect(() => {
    if (activeModal === 'auth-login') setView('login');
    if (activeModal === 'auth-register') setView('register');
  }, [activeModal]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => { if (e.key === 'Escape') closeModal(); }, [closeModal]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => { document.removeEventListener('keydown', handleKeyDown); document.body.style.overflow = ''; };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={closeModal} />
      <div className="relative z-10 w-full max-w-[380px] mx-4 animate-scale-in">
        <button onClick={closeModal} className="absolute -top-3 -right-3 z-20 h-8 w-8 rounded-full bg-[#2E3340] text-gray-400 hover:text-white hover:bg-[#2E3340] flex items-center justify-center transition-colors">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <div className="bg-[#1A1D23] border border-[#2E3340] rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-2 mb-4">
              <img src={logo} alt="CricClash" className="h-8 w-8" />
              <span className="text-white text-base font-bold">CricClash</span>
            </div>
            <h3 className="text-lg font-bold text-white">
              {view === 'login' ? 'Welcome back' : 'Create your account'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {view === 'login' ? 'Sign in to continue to CricClash' : 'Join the fantasy cricket arena'}
            </p>
          </div>

          <div className="px-6 pb-6 max-h-[70vh] overflow-y-auto">
            {view === 'login' ? (
              <LoginForm onSuccess={() => { closeModal(); navigate('/home'); }} onForgotPassword={() => { closeModal(); navigate('/forgot-password'); }} />
            ) : (
              <RegisterForm onSuccess={(email) => { closeModal(); navigate(`/verify-email?email=${encodeURIComponent(email)}`); }} />
            )}

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#2E3340]" /></div>
              <div className="relative flex justify-center text-[10px]"><span className="bg-[#1A1D23] px-3 text-gray-500 uppercase tracking-wider">or</span></div>
            </div>

            <GoogleButton />

            <p className="text-xs text-gray-500 text-center mt-4">
              {view === 'login' ? (
                <>Don't have an account?{' '}<button onClick={() => setView('register')} className="text-[#13EC5B] hover:text-[#10D450] font-medium">Create Account</button></>
              ) : (
                <>Already have an account?{' '}<button onClick={() => setView('login')} className="text-[#13EC5B] hover:text-[#10D450] font-medium">Login</button></>
              )}
            </p>

            <p className="text-[10px] text-gray-600 text-center mt-3">
              By continuing you agree to our{' '}
              <Link to="/terms" onClick={closeModal} className="text-gray-500 hover:text-[#13EC5B]">Terms</Link>
              {' & '}
              <Link to="/privacy-policy" onClick={closeModal} className="text-gray-500 hover:text-[#13EC5B]">Privacy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function LoginForm({ onSuccess, onForgotPassword }: { onSuccess: () => void; onForgotPassword: () => void }) {
  const login = useLogin();
  const navigate = useNavigate();
  const { closeModal } = useUIStore();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginData>({ resolver: zodResolver(loginSchema) });
  const onSubmit = (data: LoginData) => {
    login.mutate(data, {
      onSuccess: () => { toast.success('Welcome back!'); onSuccess(); },
      onError: (err: any) => {
        if (err.code === 'USER_NOT_CONFIRMED') {
          toast.info('Please verify your email first');
          closeModal();
          navigate(`/verify-email?email=${encodeURIComponent(data.email)}&from=login`);
        } else {
          toast.error(err.message || 'Login failed');
        }
      },
    });
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 mt-4">
      <Input label="Email" type="email" placeholder="you@example.com" fullWidth error={errors.email?.message} {...register('email')} />
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-gray-300">Password</label>
          <button type="button" onClick={onForgotPassword} className="text-[10px] text-[#13EC5B] hover:text-[#10D450]">Forgot?</button>
        </div>
        <Input type="password" placeholder="Enter password" fullWidth error={errors.password?.message} {...register('password')} />
      </div>
      <Button type="submit" fullWidth loading={login.isPending} className="bg-[#13EC5B] text-black font-bold !h-9 !text-xs">LOGIN</Button>
    </form>
  );
}

function RegisterForm({ onSuccess }: { onSuccess: (email: string) => void }) {
  const registerMutation = useRegister();
  const { register, handleSubmit, formState: { errors } } = useForm<RegisterData>({ resolver: zodResolver(registerSchema) });
  const onSubmit = (data: RegisterData) => {
    registerMutation.mutate(
      { email: data.email, password: data.password, firstName: data.firstName, lastName: data.lastName },
      { onSuccess: () => { toast.success('Account created! Check your email.'); onSuccess(data.email); }, onError: (err: Error) => toast.error(err.message || 'Registration failed') },
    );
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 mt-4">
      <div className="grid grid-cols-2 gap-2">
        <Input label="First Name" placeholder="John" fullWidth error={errors.firstName?.message} {...register('firstName')} />
        <Input label="Last Name" placeholder="Doe" fullWidth error={errors.lastName?.message} {...register('lastName')} />
      </div>
      <Input label="Email" type="email" placeholder="you@example.com" fullWidth error={errors.email?.message} {...register('email')} />
      <div className="grid grid-cols-2 gap-2">
        <Input label="Password" type="password" placeholder="Min 8 chars" fullWidth error={errors.password?.message} {...register('password')} />
        <Input label="Confirm" type="password" placeholder="Re-enter" fullWidth error={errors.confirmPassword?.message} {...register('confirmPassword')} />
      </div>
      <Button type="submit" fullWidth loading={registerMutation.isPending} className="bg-[#13EC5B] text-black font-bold !h-9 !text-xs">CREATE ACCOUNT</Button>
    </form>
  );
}

function GoogleButton() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const { closeModal } = useUIStore();
  const [loading, setLoading] = useState(false);

  const handleSuccess = async (tokenResponse: any) => {
    setLoading(true);
    try {
      const data = await apiService.googleAuth(tokenResponse.access_token, 'access_token');
      const tokens = data.tokens || data;
      if (tokens.AccessToken) {
        const { tokenStorage } = await import('../../lib/tokenStorage');
        tokenStorage.setTokens(tokens.AccessToken, tokens.RefreshToken, tokens.IdToken);
        const payload = tokens.IdToken.split('.')[1];
        const decoded = JSON.parse(atob(payload));
        const user = {
          id: decoded.sub || decoded['cognito:username'],
          email: decoded.email,
          firstName: decoded.given_name || 'User',
          lastName: decoded.family_name || '',
          role: decoded['cognito:groups']?.[0] || 'user',
          avatar: '',
          username: decoded['cognito:username'],
        };
        tokenStorage.setUser(user);
        setAuth(user, tokens.AccessToken);
        analyticsService.identify(user.id, { email: user.email, role: user.role, method: 'google' });
        analyticsService.track('login', { method: 'google' });
        closeModal();
        toast.success('Signed in with Google!');
        window.location.href = '/home';
      } else if (data.requiresProfile || data.status === 'onboarding') {
        closeModal();
        navigate('/complete-profile');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const login = useGoogleLogin({
    onSuccess: handleSuccess,
    onError: () => toast.error('Google sign-in failed'),
  });

  return (
    <button
      type="button"
      onClick={() => login()}
      disabled={loading}
      className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-[#0F1114] border border-[#2E3340] rounded-lg hover:border-[#3E4350] transition-colors disabled:opacity-60"
    >
      {loading ? (
        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <svg className="w-4 h-4" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      )}
      <span className="text-sm font-medium text-gray-300">Continue with Google</span>
    </button>
  );
}
